import type { Creature, Stage } from '@/domain/creature/types';
import { deriveLifeOutcome, deriveVisualCareAccents, type VisualCareAccent } from './life-outcome';
import { hasTemperament } from './temperament';

export type VisualPosture = 'neutral' | 'proud' | 'slouched' | 'soft' | 'spiky' | 'delicate' | 'bouncy';
export type VisualBodyBias = 'neutral' | 'sturdy' | 'rounded' | 'droopy' | 'lean' | 'delicate';
export type VisualLimbStyle = 'normal' | 'tiny-paws' | 'sturdy' | 'fins' | 'wispy' | 'jitter';
export type VisualExpressionBias = 'neutral' | 'bright' | 'smug' | 'tired' | 'defiant' | 'soft';

export interface VisualEvolutionProfile {
  stage: Stage;
  posture: VisualPosture;
  bodyBias: VisualBodyBias;
  limbStyle: VisualLimbStyle;
  expressionBias: VisualExpressionBias;
  accentHints: VisualCareAccent[];
  deformBoost: number;
}

const NEUTRAL_PROFILE: Omit<VisualEvolutionProfile, 'stage'> = {
  posture: 'neutral',
  bodyBias: 'neutral',
  limbStyle: 'normal',
  expressionBias: 'neutral',
  accentHints: [],
  deformBoost: 0,
};

export function deriveVisualEvolutionProfile(creature: Creature): VisualEvolutionProfile {
  if (
    creature.lifecycle.stage === 'egg' ||
    creature.lifecycle.stage === 'baby' ||
    creature.lifecycle.stage === 'dead'
  ) {
    return { stage: creature.lifecycle.stage, ...NEUTRAL_PROFILE };
  }

  const outcome = deriveLifeOutcome(creature);
  const tags = new Set(outcome.behaviorTags);
  const traits = new Set([...(creature.lifecycle.careTraits ?? []), ...outcome.careTraits]);
  const visualAccents = deriveVisualCareAccents(creature);

  if (traits.has('rebellious')) {
    return {
      stage: creature.lifecycle.stage,
      posture: 'spiky',
      bodyBias: 'lean',
      limbStyle: 'jitter',
      expressionBias: 'defiant',
      accentHints: visualAccents,
      deformBoost: -0.18,
    };
  }

  if (tags.has('glutton-spoiled') || traits.has('heavy')) {
    return {
      stage: creature.lifecycle.stage,
      posture: 'soft',
      bodyBias: 'rounded',
      limbStyle: 'tiny-paws',
      expressionBias: 'smug',
      accentHints: visualAccents,
      deformBoost: -0.12,
    };
  }

  if (tags.has('tidy-neglected') || traits.has('messy') || traits.has('frail')) {
    return {
      stage: creature.lifecycle.stage,
      posture: 'slouched',
      bodyBias: 'droopy',
      limbStyle: hasTemperament(outcome.temperament, 'fragile') ? 'wispy' : 'normal',
      expressionBias: 'tired',
      accentHints: visualAccents,
      deformBoost: -0.22,
    };
  }

  if (tags.has('fragile-resilient')) {
    return {
      stage: creature.lifecycle.stage,
      posture: 'delicate',
      bodyBias: 'delicate',
      limbStyle: 'wispy',
      expressionBias: 'soft',
      accentHints: visualAccents,
      deformBoost: 0.08,
    };
  }

  if (tags.has('playful-bonded') || hasTemperament(outcome.temperament, 'playful')) {
    return {
      stage: creature.lifecycle.stage,
      posture: 'bouncy',
      bodyBias: 'neutral',
      limbStyle: 'normal',
      expressionBias: 'bright',
      accentHints: visualAccents,
      deformBoost: 0.1,
    };
  }

  if (creature.lifecycle.careTier === 'bom' || creature.lifecycle.careTier === 'secreto') {
    return {
      stage: creature.lifecycle.stage,
      posture: 'proud',
      bodyBias: 'sturdy',
      limbStyle: 'sturdy',
      expressionBias: 'bright',
      accentHints: visualAccents,
      deformBoost: 0.16,
    };
  }

  return { stage: creature.lifecycle.stage, ...NEUTRAL_PROFILE, accentHints: visualAccents };
}
